import { useState } from 'react'
import { Link, useParams } from 'react-router'
import type { LineMerchandise, OrderDetailResponse, OrderLineView } from '@fitting-room/contracts'
import { ChevronLeft, Pencil, Plus, Trash2 } from 'lucide-react'
import { OrderAlerts } from '../../components/admin/orders/AlertList'
import { ChangeLogList } from '../../components/admin/orders/ChangeLogList'
import { LineFulfillment } from '../../components/admin/orders/LineFulfillment'
import { OrderAppointments } from '../../components/admin/orders/OrderAppointments'
import { MeasurementsCard, OrderHeaderCard } from '../../components/admin/orders/OrderHeader'
import { OrderProgressCard } from '../../components/admin/orders/OrderProgressCard'
import { OrderNotesEditButton, OrderNotesPanel, type OrderNotesSection } from '../../components/admin/orders/OrderNotesPanel'
import { OrderLineDialog } from '../../components/admin/orders/OrderLineDialog'
import { OrderStatusBadge } from '../../components/admin/orders/OrderStatusBadge'
import { ReasonDialog } from '../../components/admin/orders/ReasonDialog'
import { AccountingCard, OrderPayments } from '../../components/admin/payments/OrderPayments'
import { PaymentStateBadge } from '../../components/admin/payments/PaymentStateBadge'
import { ApiRequestError } from '../../lib/api'
import { useOrder, useRemoveOrderLine, useSetOrderStatus, useSupplierRefreshOnOpen } from '../../lib/adminQueries'
import { BOUTIQUE_LOCALE, boutiqueDateTime, boutiqueDayOf } from '../../lib/boutique'
import { shortDay } from '../../lib/commission'
import { formatDay } from '../../lib/format'
import {
  GROUP_LABEL,
  GROUP_ORDER,
  balanceDueSource,
  discountLabel,
  formatCents,
  formatRate,
  lineTitle,
  lineTypeLabel,
} from '../../lib/orders'
import { useStaffToken } from '../../lib/staffToken'

/**
 * One order, laid out as its invoice is (`docs/22` § "Orders"): who it is for
 * across the top; then what they bought in the invoice's groups — Dresses,
 * Accessories, Other accessories — each line by style number with its type,
 * color, size, price, custom cost, discount and total, and "Add more" under
 * each; the customer's measurements beside the other request; the totals,
 * and under them the payments.
 *
 * Everything is edited where it is shown: the header in place, a line in its
 * sheet, the measurements, the other request and the note in one side
 * panel, a payment's Shopify order number on its row. Under each line its way
 * to the customer (`LineFulfillment`); what needs doing about the order at
 * the top; in the aside the appointments it belongs to, the owner's
 * accounting mark, the commission, the note — and the history, once there is
 * any. At the top of the aside, Order progress: payment, invoice, dresses and
 * pickup, with Download PDF, Email invoice and Cancel order among its actions
 * (CMP-9 § 1) — first on a phone too, ahead of the lines.
 */
export function OrderDetailPage() {
  const { orderId = '' } = useParams<{ orderId: string }>()
  const { token } = useStaffToken()
  const query = useOrder(token, orderId)
  // Lines linked to Jovita catch up as the order opens (at most every ten minutes).
  useSupplierRefreshOnOpen(token)

  return (
    <div>
      <Link to="/admin/orders" className="st-btn-quiet print-hide -ml-2 mb-3 inline-flex">
        <ChevronLeft size={14} aria-hidden />
        Orders
      </Link>
      {query.isPending ? <p className="st-meta">Loading…</p> : null}
      {query.isError ? (
        <p className="st-alert" role="alert">
          {query.error instanceof ApiRequestError && query.error.status === 404
            ? "There's no order here. It may be another boutique's, or the link is wrong."
            : 'Couldn’t load this order. Try again in a moment.'}
        </p>
      ) : null}
      {query.data ? <OrderView detail={query.data} /> : null}
    </div>
  )
}

function OrderView({ detail }: { readonly detail: OrderDetailResponse }) {
  const { token, role } = useStaffToken()
  const { order } = detail
  const status = useSetOrderStatus(token, order.id)
  const [asking, setAsking] = useState<'cancel' | 'restore' | null>(null)
  // Which Edit opened the measurements-and-note panel; `null` while it is shut.
  const [notesAt, setNotesAt] = useState<OrderNotesSection | null>(null)
  const pieces = detail.lines.filter((line) => line.rackItemId).length
  const gone = detail.lines.filter((line) => line.fulfillment === 'picked_up').length

  return (
    <>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="st-eyebrow mb-1">Invoice · written {formatDay(boutiqueDayOf(order.createdAt), BOUTIQUE_LOCALE)}</div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="st-display text-3xl tabular-nums">{order.orderNo}</h1>
            <OrderStatusBadge status={order.status} />
          </div>
        </div>
      </div>

      {order.status === 'canceled' ? (
        <div className="st-notice mb-5" role="status">
          <span className="font-medium">This order is canceled</span>
          {order.canceledAt ? <span> ({boutiqueDateTime(order.canceledAt)})</span> : null}
          {order.canceledReason ? <span>: {order.canceledReason}</span> : null}
          <span className="mt-1 block text-xs">
            Anything it held is back on the rack. Restoring it holds those pieces again, if nobody else has them.
          </span>
        </div>
      ) : null}

      {order.status === 'completed' ? (
        <div className="st-ok mb-5" role="status">
          Completed: everything has gone home with the customer and it&rsquo;s paid in full. Undoing a pickup, or a refund,
          opens it again.
        </div>
      ) : null}

      <OrderAlerts alerts={detail.alerts} />

      {/* Order progress is the one aside card that comes first on a narrow
          screen: a grid item of its own, put in the aside's column on a wide one. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:grid-rows-[auto_1fr]">
        <div className="print-hide min-w-0 lg:col-start-2 lg:row-start-1">
          <OrderProgressCard detail={detail} onCancel={() => setAsking('cancel')} onRestore={() => setAsking('restore')} />
        </div>
        <div className="min-w-0 space-y-4 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          <OrderHeaderCard detail={detail} />
          <LineGroups detail={detail} />
          <MeasurementsCard detail={detail} onEdit={setNotesAt} />
          <TotalsCard detail={detail} />
          <OrderPayments detail={detail} />
        </div>
        <aside className="min-w-0 space-y-4 lg:col-start-2 lg:row-start-2">
          <OrderAppointments detail={detail} />
          <AccountingCard detail={detail} />
          <CommissionCard detail={detail} />
          <section className="st-card px-4 py-3" data-testid="order-note">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <h2 className="st-eyebrow">Note</h2>
              <OrderNotesEditButton detail={detail} section="note" onEdit={setNotesAt} />
            </div>
            <p className="whitespace-pre-wrap break-words text-sm">{order.note || <span className="st-meta">None</span>}</p>
          </section>
          {detail.changeLog.length > 0 ? (
            <section className="st-card px-4 py-3" aria-labelledby="order-history">
              <h2 id="order-history" className="st-eyebrow mb-2.5">
                Change history
              </h2>
              <ChangeLogList logs={detail.changeLog} />
            </section>
          ) : null}
        </aside>
      </div>

      <OrderNotesPanel detail={detail} section={order.status === 'open' ? notesAt : null} onClose={() => setNotesAt(null)} />

      <ReasonDialog
        open={asking === 'cancel'}
        title={`Cancel ${order.orderNo}?`}
        placeholder="Like: wedding postponed, they're not going ahead"
        confirmLabel="Cancel order"
        pendingLabel="Canceling…"
        tone="alert"
        pending={status.isPending}
        error={asking === 'cancel' ? status.error?.message : undefined}
        onClose={() => {
          status.reset()
          setAsking(null)
        }}
        onConfirm={(reason) => status.mutate({ to: 'cancel', reason }, { onSuccess: () => setAsking(null) })}
      >
        {gone > 0 && role !== 'owner'
          ? `${gone === 1 ? 'A line has' : `${gone} lines have`} already gone home with the customer, so canceling is a return — ask an owner. `
          : null}
        {pieces > 0
          ? `${pieces === 1 ? 'The piece' : `All ${pieces} pieces`} it holds go back on the rack, where anyone can buy them.`
          : 'Nothing it holds goes anywhere; it just stops being an open order.'}{' '}
        {detail.money.paidMinor > 0
          ? `No money moves: ${formatCents(detail.money.paidMinor)} has been paid on it, and giving any of it back is a refund you record after.`
          : 'No money moves: nothing has been paid on it.'}
      </ReasonDialog>
      <ReasonDialog
        open={asking === 'restore'}
        title={`Restore ${order.orderNo}?`}
        placeholder="Like: canceled by mistake, they're going ahead after all"
        confirmLabel="Restore order"
        pendingLabel="Restoring…"
        pending={status.isPending}
        error={asking === 'restore' ? status.error?.message : undefined}
        onClose={() => {
          status.reset()
          setAsking(null)
        }}
        onConfirm={(reason) => status.mutate({ to: 'restore', reason }, { onSuccess: () => setAsking(null) })}
      >
        {pieces > 0
          ? `Its ${pieces === 1 ? 'piece is' : `${pieces} pieces are`} held again. If one has gone to someone else since, it stays canceled and you'll see which.`
          : 'It goes back to being an open order.'}
      </ReasonDialog>
    </>
  )
}

/**
 * The lines in the invoice's groups. On an open order the three groups a
 * stylist adds to are always drawn, each with "Add more"; the older `other`
 * group (alterations, other) only when it has lines.
 */
function LineGroups({ detail }: { readonly detail: OrderDetailResponse }) {
  const { token } = useStaffToken()
  const editable = detail.order.status === 'open'
  const [adding, setAdding] = useState<LineMerchandise | null>(null)
  const [editingLine, setEditingLine] = useState<OrderLineView | null>(null)
  const [removing, setRemoving] = useState<OrderLineView | null>(null)
  const remove = useRemoveOrderLine(token, detail.order.id)
  const groups = GROUP_ORDER.filter((group) => detail.lines.some((line) => line.merchandise === group) || (editable && group !== 'other'))

  return (
    <section className="space-y-3" aria-label="What they bought">
      {detail.lines.length === 0 && !editable ? <p className="st-card st-meta px-4 py-6 text-center text-sm">Nothing on this order.</p> : null}
      {groups.map((group) => {
        const lines = detail.lines.filter((line) => line.merchandise === group)
        return (
          <div key={group} className="st-card overflow-hidden" data-group={group}>
            <div className="st-invoice-band flex items-center justify-between gap-2">
              <h2>{GROUP_LABEL[group]}</h2>
            </div>
            {lines.length > 0 ? (
              <ul className="divide-y divide-[color:var(--st-rule)] px-4">
                {lines.map((line) => (
                  <LineRow
                    key={line.id}
                    detail={detail}
                    line={line}
                    editable={editable}
                    onEdit={() => setEditingLine(line)}
                    onRemove={() => setRemoving(line)}
                  />
                ))}
              </ul>
            ) : (
              <p className="st-meta px-4 py-3 text-sm">{group === 'shoes' ? 'No bridal heels.' : 'None yet.'}</p>
            )}
            {editable && group !== 'other' ? (
              <div className="print-hide border-t px-2 py-1.5" style={{ borderColor: 'var(--st-rule)' }}>
                <button type="button" className="st-btn-quiet" onClick={() => setAdding(group)}>
                  <Plus size={14} aria-hidden />
                  {group === 'shoes' ? 'Add bridal heels' : 'Add more'}
                </button>
              </div>
            ) : null}
          </div>
        )
      })}

      <OrderLineDialog orderId={detail.order.id} merchandise={adding ?? 'dress'} open={adding !== null} onClose={() => setAdding(null)} />
      <OrderLineDialog orderId={detail.order.id} line={editingLine ?? undefined} open={editingLine !== null} onClose={() => setEditingLine(null)} />
      <ReasonDialog
        open={removing !== null}
        title={removing ? `Take ${lineTitle(removing)} off the order?` : ''}
        placeholder="Like: rang up the wrong style"
        confirmLabel="Take it off"
        pendingLabel="Taking it off…"
        tone="alert"
        pending={remove.isPending}
        error={remove.error?.message}
        onClose={() => {
          remove.reset()
          setRemoving(null)
        }}
        onConfirm={(reason) => removing && remove.mutate({ lineId: removing.id, reason }, { onSuccess: () => setRemoving(null) })}
      >
        {removing?.rackItemId ? 'The piece goes back on the rack.' : null}
      </ReasonDialog>
    </section>
  )
}

/**
 * One line, by style number, with the invoice's columns under it — two to a
 * row on a phone, all six across on anything wider.
 */
function LineRow({
  detail,
  line,
  editable,
  onEdit,
  onRemove,
}: {
  readonly detail: OrderDetailResponse
  readonly line: OrderLineView
  readonly editable: boolean
  readonly onEdit: () => void
  readonly onRemove: () => void
}) {
  const title = lineTitle(line)
  const discount = discountLabel(line.discountKind, line.discountValue)
  const extra = [line.styleCode && line.description !== line.styleCode ? line.description : null, !line.taxable ? 'not taxed' : null, !line.commissionable ? 'no commission' : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1 py-3" data-line-id={line.id} data-kind={line.kind}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium" data-testid="line-title">
            {title}
            {line.quantity > 1 ? ` × ${line.quantity}` : ''}
          </span>
          <span className="st-meta text-xs" data-testid="line-type">
            {lineTypeLabel(line.kind)}
          </span>
        </div>
        {extra ? <div className="st-meta text-xs">{extra}</div> : null}
        <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-sm sm:grid-cols-6">
          <Cell label="Color" value={line.color || '—'} />
          <Cell label="Size" value={line.size || '—'} />
          <Cell label="Price" value={formatCents(line.grossMinor)} />
          <Cell label="Custom cost" value={line.customCostMinor > 0 ? formatCents(line.customCostMinor) : '—'} />
          <Cell
            label="Discount"
            value={discount ? `${discount}${line.discountKind === 'percent' ? ` (${formatCents(line.discountMinor)})` : ''}` : '—'}
          />
          <Cell label="Total" value={formatCents(line.netMinor)} strong />
        </dl>
      </div>
      {editable ? (
        <div className="print-hide flex gap-1">
          <button type="button" className="st-iconbtn" aria-label={`Edit ${title}`} onClick={onEdit}>
            <Pencil size={14} aria-hidden />
          </button>
          <button type="button" className="st-iconbtn" aria-label={`Take ${title} off the order`} onClick={onRemove}>
            <Trash2 size={14} aria-hidden />
          </button>
        </div>
      ) : null}
      {/* Its own row under the line, full width, so the steps and the buttons
          never squeeze the price on a phone. */}
      <LineFulfillment detail={detail} line={line} />
    </li>
  )
}

function Cell({ label, value, strong }: { readonly label: string; readonly value: string; readonly strong?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="st-meta text-xs">{label}</dt>
      <dd className={`break-words tabular-nums ${strong ? 'font-medium' : ''}`}>{value}</dd>
    </div>
  )
}

/**
 * The order's money, at the order's own tax rate — never today's setting —
 * and, under the total, what has been paid against it, what is left, and
 * when that is due and why that day.
 */
function TotalsCard({ detail }: { readonly detail: OrderDetailResponse }) {
  const { totals, money } = detail
  const canceled = detail.order.status === 'canceled'
  return (
    <section className="st-card overflow-hidden text-sm" aria-labelledby="order-totals">
      <h2 id="order-totals" className="st-invoice-band">
        Totals
      </h2>
      <div className="px-4 py-3">
        <Row label="Subtotal" value={formatCents(totals.subtotalMinor)} />
        <Row
          label={`Tax · ${formatRate(totals.taxRateBps)}${totals.taxableMinor !== totals.subtotalMinor ? ` of ${formatCents(totals.taxableMinor)}` : ''}`}
          value={formatCents(totals.taxMinor)}
        />
        <div className="mt-1.5 flex justify-between border-t pt-2 text-base" style={{ borderColor: 'var(--st-rule)' }}>
          <span className="font-medium">Total</span>
          <span className="st-display text-xl tabular-nums" data-testid="order-total">
            {formatCents(totals.totalMinor)}
          </span>
        </div>
        {totals.discountMinor > 0 || totals.customCostMinor > 0 ? (
          <p className="st-meta pb-1 text-xs">
            Line totals include {totals.customCostMinor > 0 ? `${formatCents(totals.customCostMinor)} of custom cost` : ''}
            {totals.customCostMinor > 0 && totals.discountMinor > 0 ? ' and ' : ''}
            {totals.discountMinor > 0 ? `${formatCents(totals.discountMinor)} off in discounts` : ''}.
          </p>
        ) : null}
        <Row label="Paid" value={formatCents(money.paidMinor)} />
        {canceled ? (
          <p className="st-meta py-0.5 text-xs">
            Canceled, so nothing more is owed.{money.paidMinor > 0 ? ' Whatever goes back to the customer is recorded as a refund.' : ''}
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 py-0.5">
              <span className="flex items-center gap-2">
                <span className="st-meta">{money.balanceMinor < 0 ? 'Overpaid by' : 'Remaining balance'}</span>
                <PaymentStateBadge state={money.paymentState} totalMinor={money.totalMinor} paidMinor={money.paidMinor} />
              </span>
              <span className="font-medium tabular-nums" data-testid="order-balance">
                {formatCents(Math.abs(money.balanceMinor))}
              </span>
            </div>
            {/* Present only while a balance remains: nothing owed, no day for it to be due. */}
            {detail.balanceDue ? (
              <div className="flex items-start justify-between gap-3 py-0.5" data-testid="order-balance-due">
                <span className="st-meta">
                  Balance due
                  <span className="block text-xs">{balanceDueSource(detail.balanceDue.source)}</span>
                </span>
                <span className="tabular-nums">{shortDay(detail.balanceDue.date)}</span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  )
}

function Row({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="st-meta">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

/**
 * The commission payout this order is in — the owner's to read, so the API
 * sends it to an owner only. Said plainly when the order has stopped being
 * paid in full since: nothing is taken back by itself, and the payout keeps
 * what it was calculated at.
 */
function CommissionCard({ detail }: { readonly detail: OrderDetailResponse }) {
  const ref = detail.commission
  if (!ref) return null
  const confirmed = ref.status === 'confirmed'
  const day = shortDay(boutiqueDayOf(confirmed ? (ref.confirmedAt ?? ref.createdAt) : ref.createdAt))
  return (
    <section className="st-card px-4 py-3 text-sm" data-testid="order-commission">
      <h2 className="st-eyebrow mb-1.5">Commission</h2>
      <p>
        {confirmed
          ? `Paid out to ${ref.staffName}: ${formatCents(ref.amountMinor)}, confirmed ${day}.`
          : `In a pending payout to ${ref.staffName}: ${formatCents(ref.amountMinor)}, calculated ${day}.`}
      </p>
      {detail.money.balanceMinor > 0 || detail.order.status === 'canceled' ? (
        <p className="st-meta mt-1.5 text-xs" style={{ color: '#8d3f2c' }}>
          {detail.order.status === 'canceled' ? 'It’s canceled now' : 'It’s no longer paid in full'}; the payout keeps what it
          was calculated at.{confirmed ? '' : ' Discard it to take this order out.'}
        </p>
      ) : null}
      <Link to="/admin/commission" className="st-btn-quiet -ml-2 mt-1 inline-flex">
        Open Commission
      </Link>
    </section>
  )
}
